(() => {
  const ROOT_SELECTOR = '[data-tz-mobile-nav]';

  /* ==========================================================
     HELPERS
     ========================================================== */

  function safeQuery(selector) {
    if (!selector) return null;

    try {
      return document.querySelector(selector);
    } catch (error) {
      console.warn('[TZ Mobile Nav] Invalid selector:', selector);
      return null;
    }
  }

  function normalizePath(url) {
    try {
      const parsed = new URL(url, window.location.origin);
      let path = parsed.pathname || '/';

      if (path.length > 1) {
        path = path.replace(/\/+$/, '');
      }

      return path;
    } catch (error) {
      return '/';
    }
  }

  function getElementCenter(element) {
    if (!element) return null;

    const rect = element.getBoundingClientRect();

    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2
    };
  }

  /* ==========================================================
     TOYZONE CUSTOM HEADER DRAWER
     ========================================================== */

  function getToyZoneDrawer() {
    return document.getElementById('tzDrawer');
  }

  function getToyZoneOverlay() {
    return document.getElementById('tzOverlay');
  }

  function isToyZoneDrawerOpen() {
    const drawer = getToyZoneDrawer();

    return Boolean(
      drawer &&
      drawer.classList.contains('active')
    );
  }

  function setCategoriesState(root, active) {
    const button =
      root.querySelector('[data-tz-categories]');

    if (!button) return;

    button.classList.toggle(
      'is-active',
      active
    );

    button.setAttribute(
      'aria-expanded',
      active ? 'true' : 'false'
    );
  }

  function openToyZoneDrawer(root) {
    const drawer = getToyZoneDrawer();
    const overlay = getToyZoneOverlay();

    if (!drawer || !overlay) {
      return false;
    }

    drawer.classList.add('active');
    overlay.classList.add('active');

    document.body.style.overflow =
      'hidden';

    setCategoriesState(
      root,
      true
    );

    return true;
  }

  function closeToyZoneDrawer(root) {
    const drawer = getToyZoneDrawer();
    const overlay = getToyZoneOverlay();

    if (!drawer || !overlay) {
      return false;
    }

    drawer.classList.remove('active');
    overlay.classList.remove('active');

    document.body.style.overflow =
      '';

    setCategoriesState(
      root,
      false
    );

    return true;
  }

  function watchToyZoneDrawer(root) {
    const drawer =
      getToyZoneDrawer();

    if (
      !drawer ||
      drawer.dataset.tzBottomNavObserved ===
        'true'
    ) {
      setCategoriesState(
        root,
        isToyZoneDrawerOpen()
      );

      return;
    }

    drawer.dataset.tzBottomNavObserved =
      'true';

    const observer =
      new MutationObserver(() => {
        setCategoriesState(
          root,
          drawer.classList.contains(
            'active'
          )
        );
      });

    observer.observe(
      drawer,
      {
        attributes: true,
        attributeFilter: ['class']
      }
    );

    setCategoriesState(
      root,
      drawer.classList.contains(
        'active'
      )
    );
  }

  /* ==========================================================
     FALLBACK MENU
     ========================================================== */

  function findFallbackMenuTrigger() {
    const selectors = [
      '.mobile-submenu-label',
      'header-drawer summary',
      '.menu-drawer-container summary',
      '[data-menu-drawer-trigger]',
      '[aria-controls*="menu-drawer"]',
      'button[aria-label*="menu" i]',
      '.header__icon--menu'
    ];

    for (
      const selector of selectors
    ) {
      const found =
        safeQuery(selector);

      if (found) {
        return found;
      }
    }

    return null;
  }

  /* ==========================================================
     NATIVE TOYZONE / BOOSTER CART DRAWER

     IMPORTANT:

     Your header renders Booster's minicart.
     The real mobile cart control is:

     .page__header--mobile .minicart__label

     We click that SAME native element.
     ========================================================== */

  function findFallbackCartTrigger() {
    const selectors = [

      /* Exact ToyZone mobile header cart */
      '.page__header--mobile .minicart__label',

      '.page__header--mobile label.minicart__label',

      '.page__header--mobile .minicart__button .minicart__label',

      /* Booster fallbacks */
      '.minicart__button .minicart__label',

      '.minicart__label',

      /* Other possible drawer triggers */
      '[data-cart-drawer-trigger]',

      'button[aria-controls*="CartDrawer"]',

      '[aria-controls="CartDrawer"]',

      '#cart-icon-bubble',

      '.header__icon--cart'
    ];

    for (
      const selector of selectors
    ) {
      const found =
        safeQuery(selector);

      if (found) {
        return found;
      }
    }

    return null;
  }

  /* ==========================================================
     CART COUNT
     ========================================================== */

  async function getCart() {
    try {
      const shopRoot =
        window.Shopify &&
        window.Shopify.routes &&
        window.Shopify.routes.root
          ? window.Shopify.routes.root
          : '/';

      const response =
        await fetch(
          `${shopRoot}cart.js`,
          {
            credentials:
              'same-origin',

            headers: {
              Accept:
                'application/json'
            }
          }
        );

      if (!response.ok) {
        return null;
      }

      return await response.json();

    } catch (error) {
      return null;
    }
  }

  function updateCount(
    root,
    count
  ) {
    const badge =
      root.querySelector(
        '[data-tz-cart-count]'
      );

    if (!badge) return;

    const numericCount =
      Number(count) || 0;

    badge.textContent =
      numericCount > 99
        ? '99+'
        : String(numericCount);

    badge.hidden =
      numericCount <= 0;
  }

  function mirrorThemeCount(root) {
    const selector =
      root.dataset
        .cartCountSelector;

    const existing =
      safeQuery(selector);

    if (!existing) {
      return false;
    }

    const text =
      String(
        existing.textContent || ''
      ).replace(
        /\D/g,
        ''
      );

    if (text === '') {
      return false;
    }

    updateCount(
      root,
      Number(text)
    );

    return true;
  }

  async function syncCartCount(root) {
    if (
      mirrorThemeCount(root)
    ) {
      return;
    }

    const cart =
      await getCart();

    if (cart) {
      updateCount(
        root,
        cart.item_count
      );
    }
  }

  /* ==========================================================
     ACTIVE URL
     ========================================================== */

  function clearPermanentActiveItems(
    root
  ) {
    root
      .querySelectorAll(
        '[data-tz-nav-item]'
      )
      .forEach(item => {

        if (
          item.dataset.navType !==
          'categories'
        ) {
          item.classList.remove(
            'is-active'
          );
        }

      });
  }

  function setActiveItem(root) {
    const currentPath =
      normalizePath(
        window.location.href
      );

    clearPermanentActiveItems(
      root
    );

    const home =
      root.querySelector(
        '[data-nav-type="home"]'
      );

    const deals =
      root.querySelector(
        '[data-nav-type="deals"]'
      );

    const account =
      root.querySelector(
        '[data-nav-type="account"]'
      );

    if (
      currentPath === '/' &&
      home
    ) {
      home.classList.add(
        'is-active'
      );

      return;
    }

    if (
      deals &&
      deals.tagName === 'A'
    ) {
      const dealsPath =
        normalizePath(
          deals.href
        );

      if (
        dealsPath !== '/' &&
        (
          currentPath ===
            dealsPath ||

          currentPath.startsWith(
            `${dealsPath}/`
          )
        )
      ) {
        deals.classList.add(
          'is-active'
        );

        return;
      }
    }

    if (
      account &&
      currentPath.startsWith(
        '/account'
      )
    ) {
      account.classList.add(
        'is-active'
      );
    }
  }

  /* ==========================================================
     MORE LIQUID-GLASS SHEET
     ========================================================== */

  function openMore(root) {
    const sheet =
      root.querySelector(
        '[data-tz-more]'
      );

    const button =
      root.querySelector(
        '[data-tz-more-open]'
      );

    if (!sheet) return;

    sheet.classList.add(
      'is-open'
    );

    sheet.setAttribute(
      'aria-hidden',
      'false'
    );

    if (button) {

      button.setAttribute(
        'aria-expanded',
        'true'
      );

      button.classList.add(
        'is-active'
      );
    }

    document.documentElement
      .classList.add(
        'tz-mobile-nav-open'
      );

    const closeButton =
      sheet.querySelector(
        '.tz-mobile-more__close'
      );

    window.setTimeout(
      () => {

        if (closeButton) {
          closeButton.focus();
        }

      },
      30
    );
  }

  function closeMore(root) {
    const sheet =
      root.querySelector(
        '[data-tz-more]'
      );

    const button =
      root.querySelector(
        '[data-tz-more-open]'
      );

    if (!sheet) return;

    sheet.classList.remove(
      'is-open'
    );

    sheet.setAttribute(
      'aria-hidden',
      'true'
    );

    if (button) {

      button.setAttribute(
        'aria-expanded',
        'false'
      );

      button.classList.remove(
        'is-active'
      );
    }

    document.documentElement
      .classList.remove(
        'tz-mobile-nav-open'
      );
  }

  /* ==========================================================
     CATEGORIES
     ========================================================== */

  function triggerCategories(root) {

    if (
      isToyZoneDrawerOpen()
    ) {
      closeToyZoneDrawer(root);

      return;
    }

    if (
      openToyZoneDrawer(root)
    ) {
      return;
    }

    const configured =
      safeQuery(
        root.dataset
          .categoriesSelector
      );

    const trigger =
      configured ||
      findFallbackMenuTrigger();

    if (trigger) {

      trigger.click();

      setCategoriesState(
        root,
        true
      );

      return;
    }

    console.warn(
      '[TZ Mobile Nav] Could not find #tzDrawer/#tzOverlay or another mobile menu trigger.'
    );
  }

  /* ==========================================================
     CART DRAWER

     THIS IS THE IMPORTANT UPDATED PART.
     ========================================================== */

  function triggerCart(root) {

    /*
      Prefer our ToyZone custom mini-cart drawer
      if it has been installed globally.

      If it is not present yet, keep the original
      working Booster minicart fallback below.
    */

    if (
      window.TZCartDrawer &&
      typeof window.TZCartDrawer.open ===
        'function'
    ) {

      window.TZCartDrawer.open();

      return;
    }

    /*
      STEP 1

      Find the EXACT native cart label
      rendered inside your mobile header.

      Clicking this is equivalent to
      physically pressing the cart icon
      in your existing ToyZone header.
    */

    const nativeLabel =

      document.querySelector(
        '.page__header--mobile .minicart__label'
      ) ||

      document.querySelector(
        '.page__header--mobile label.minicart__label'
      ) ||

      document.querySelector(
        '.page__header--mobile .minicart__button .minicart__label'
      ) ||

      document.querySelector(
        '.minicart__button .minicart__label'
      ) ||

      document.querySelector(
        '.minicart__label'
      );


    if (nativeLabel) {

      nativeLabel.click();

      return;
    }


    /*
      STEP 2

      Booster normally controls the drawer
      using its minicart checkbox.

      If for some reason the label isn't
      available, click the native checkbox.
    */

    const minicartInput =

      document.querySelector(
        '.page__header--mobile [data-minicart-input]'
      ) ||

      document.querySelector(
        '[data-minicart-input]'
      );


    if (minicartInput) {

      if (
        typeof minicartInput.click ===
        'function'
      ) {

        minicartInput.click();

      } else {

        minicartInput.checked =
          !minicartInput.checked;

        minicartInput.dispatchEvent(
          new Event(
            'input',
            {
              bubbles: true
            }
          )
        );

        minicartInput.dispatchEvent(
          new Event(
            'change',
            {
              bubbles: true
            }
          )
        );
      }

      return;
    }


    /*
      STEP 3

      Only use the Theme Editor selector
      if the native Booster controls could
      not be found.

      IMPORTANT:
      Do NOT click a normal /cart link.
    */

    const configured =
      safeQuery(
        root.dataset.cartSelector
      );


    if (configured) {

      const href =

        configured.tagName === 'A'
          ? configured.getAttribute(
              'href'
            )
          : '';


      if (
        !href ||
        normalizePath(href) !==
          '/cart'
      ) {

        configured.click();

        return;
      }
    }


    /*
      STEP 4

      Generic fallback drawer triggers.
    */

    const fallback =
      findFallbackCartTrigger();


    if (fallback) {

      fallback.click();

      return;
    }


    /*
      IMPORTANT:

      There is intentionally NO:

      window.location.href = '/cart'

      here.

      If we cannot find the drawer,
      we stay on the page instead of
      incorrectly loading /cart.
    */

    console.warn(
      '[TZ Mobile Nav] Could not find ToyZone/Booster native cart drawer control.'
    );
  }

  /* ==========================================================
     SHOOTING STAR ADD-TO-CART ANIMATION
     ========================================================== */

  function getAddToCartButton(
    target
  ) {
    if (
      !(target instanceof Element)
    ) {
      return null;
    }

    return target.closest(
      [
        '[data-tz-add-to-cart]',
        '[data-add-to-cart]',
        'button[name="add"]',
        'button[type="submit"][name="add"]',
        'product-form button[type="submit"]',
        '.product-form__submit',
        '.quick-add__submit',
        '.ajax-submit',
        '.add-to-cart'
      ].join(',')
    );
  }

  function impactCart(root) {
    const cartButton =
      root.querySelector(
        '[data-nav-type="cart"]'
      );

    if (!cartButton) return;

    cartButton.classList.remove(
      'tz-cart-impact'
    );

    void cartButton.offsetWidth;

    cartButton.classList.add(
      'tz-cart-impact'
    );

    const badge =
      cartButton.querySelector(
        '[data-tz-cart-count]'
      );

    if (badge) {

      badge.classList.remove(
        'tz-cart-badge-pop'
      );

      void badge.offsetWidth;

      badge.classList.add(
        'tz-cart-badge-pop'
      );
    }

    window.setTimeout(
      () => {

        cartButton.classList.remove(
          'tz-cart-impact'
        );

        if (badge) {
          badge.classList.remove(
            'tz-cart-badge-pop'
          );
        }

      },
      700
    );
  }

  function launchAddToCartAnimation(
    root,
    sourceButton
  ) {
    if (
      !root ||
      !sourceButton ||
      window
        .matchMedia(
          '(min-width: 750px)'
        )
        .matches
    ) {
      return;
    }

    const cartButton =
      root.querySelector(
        '[data-nav-type="cart"]'
      );

    if (!cartButton) return;

    const cartIcon =
      cartButton.querySelector(
        '.tz-mobile-nav__icon'
      ) ||
      cartButton;

    const from =
      getElementCenter(
        sourceButton
      );

    const to =
      getElementCenter(
        cartIcon
      );

    if (!from || !to) return;

    const star =
      document.createElement(
        'span'
      );

    star.className =
      'tz-atc-shooting-star';

    star.setAttribute(
      'aria-hidden',
      'true'
    );

    document.body.appendChild(
      star
    );

    const dx =
      to.x - from.x;

    const dy =
      to.y - from.y;

    const distance =
      Math.hypot(
        dx,
        dy
      );

    const angle =
      Math.atan2(
        dy,
        dx
      ) *
      180 /
      Math.PI;

    const midX =
      from.x +
      dx * 0.54;

    const midY =
      from.y +
      dy * 0.54 -
      Math.min(
        100,
        Math.max(
          34,
          distance * 0.14
        )
      );

    const duration = 880;

    star.style.left =
      `${from.x}px`;

    star.style.top =
      `${from.y}px`;

    const animation =
      star.animate(
        [
          {
            left:
              `${from.x}px`,

            top:
              `${from.y}px`,

            opacity: 0,

            transform:
              `translate(-50%, -50%) rotate(${angle}deg) scale(.55)`
          },

          {
            left:
              `${
                from.x +
                dx * 0.08
              }px`,

            top:
              `${
                from.y +
                dy * 0.08 -
                8
              }px`,

            opacity: 1,

            transform:
              `translate(-50%, -50%) rotate(${angle}deg) scale(1.12)`,

            offset: 0.12
          },

          {
            left:
              `${midX}px`,

            top:
              `${midY}px`,

            opacity: 1,

            transform:
              `translate(-50%, -50%) rotate(${angle}deg) scale(1)`
          },

          {
            left:
              `${to.x}px`,

            top:
              `${to.y}px`,

            opacity: 1,

            transform:
              `translate(-50%, -50%) rotate(${angle}deg) scale(.56)`
          }
        ],

        {
          duration,

          easing:
            'cubic-bezier(.22,.72,.18,1)',

          fill:
            'forwards'
        }
      );

    animation.onfinish =
      () => {

        star.remove();

        impactCart(root);

        syncCartCount(root);
      };
  }

  function bindAddToCartAnimation(
    root
  ) {
    if (
      root.dataset
        .tzAtcAnimationBound ===
      'true'
    ) {
      return;
    }

    root.dataset
      .tzAtcAnimationBound =
      'true';

    document.addEventListener(
      'click',

      event => {

        const button =
          getAddToCartButton(
            event.target
          );

        if (!button) return;

        if (
          button.disabled ||
          button.getAttribute(
            'aria-disabled'
          ) === 'true'
        ) {
          return;
        }

        launchAddToCartAnimation(
          root,
          button
        );
      },

      true
    );
  }

  /* ==========================================================
     BIND SECTION
     ========================================================== */

  function bindRoot(root) {

    if (
      !root ||
      root.dataset.tzBound ===
        'true'
    ) {
      return;
    }

    root.dataset.tzBound =
      'true';

    setActiveItem(root);

    syncCartCount(root);

    watchToyZoneDrawer(root);

    bindAddToCartAnimation(root);


    const categoriesButton =
      root.querySelector(
        '[data-tz-categories]'
      );


    if (categoriesButton) {

      categoriesButton
        .addEventListener(
          'click',

          () => {

            closeMore(root);

            triggerCategories(
              root
            );
          }
        );
    }


    const cartButton =
      root.querySelector(
        '[data-tz-cart]'
      );


    if (cartButton) {

      cartButton
        .addEventListener(
          'click',

          event => {

            /*
              Prevent any accidental
              link/navigation behaviour.
            */

            event.preventDefault();

            event.stopPropagation();

            closeMore(root);

            triggerCart(root);
          }
        );
    }


    const moreButton =
      root.querySelector(
        '[data-tz-more-open]'
      );


    if (moreButton) {

      moreButton
        .addEventListener(
          'click',

          () => {

            if (
              root
                .querySelector(
                  '[data-tz-more]'
                )
                ?.classList
                .contains(
                  'is-open'
                )
            ) {

              closeMore(root);

            } else {

              if (
                isToyZoneDrawerOpen()
              ) {
                closeToyZoneDrawer(
                  root
                );
              }

              openMore(root);
            }
          }
        );
    }


    root
      .querySelectorAll(
        '[data-tz-more-close]'
      )
      .forEach(
        button => {

          button.addEventListener(
            'click',

            () =>
              closeMore(root)
          );

        }
      );


    document.addEventListener(
      'keydown',

      event => {

        if (
          event.key !==
          'Escape'
        ) {
          return;
        }

        closeMore(root);

        if (
          isToyZoneDrawerOpen()
        ) {
          closeToyZoneDrawer(
            root
          );
        }
      }
    );


    const existingCount =
      safeQuery(
        root.dataset
          .cartCountSelector
      );


    if (existingCount) {

      const observer =
        new MutationObserver(
          () => {

            mirrorThemeCount(
              root
            );

          }
        );

      observer.observe(
        existingCount,

        {
          childList: true,
          subtree: true,
          characterData: true
        }
      );
    }


    [
      'cart:refresh',
      'cart:updated',
      'cart-drawer:updated',
      'ajaxProduct:added',
      'product:added'
    ].forEach(
      eventName => {

        document.addEventListener(
          eventName,

          () =>
            syncCartCount(
              root
            )
        );

      }
    );


    window.addEventListener(
      'pageshow',

      () => {

        setActiveItem(root);

        syncCartCount(root);
      }
    );
  }


  function initAll(
    scope = document
  ) {

    scope
      .querySelectorAll(
        ROOT_SELECTOR
      )
      .forEach(
        bindRoot
      );
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',

      () =>
        initAll()
    );

  } else {

    initAll();
  }


  document.addEventListener(
    'shopify:section:load',

    event => {

      initAll(
        event.target
      );

    }
  );

})();
